import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { FormProvider, useForm } from "react-hook-form";

import { FormData } from "../core/types";
import {
  InitialFieldDataProvider,
  useInitialFieldData,
} from "../data/context/InitialFieldDataContext";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

import {
  FieldRolloverContext,
  FieldRolloverContextValue,
  useFieldRolloverContext,
} from "./FieldRolloverContext";
import {
  FormMethodsContext,
  FormMethodsContextValue,
  useFormMethodsContext,
} from "./FormMethodsContext";
import { useTemplateManagement, useTemplateSwitching } from "./hooks";
import {
  InitialValuesContext,
  InitialValuesContextValue,
  useInitialValuesContext,
} from "./InitialValuesContext";
import {
  TemplateContext,
  TemplateContextValue,
  useTemplateContext,
} from "./TemplateContext";

interface FormEngineProviderProps {
  children: ReactNode;
  initialValues?: Record<string, unknown>;
}

interface FormEngineProviderCoreProps {
  children: ReactNode;
  templateManagement: ReturnType<typeof useTemplateManagement>;
}

const FormEngineProviderCore: React.FC<FormEngineProviderCoreProps> = ({
  children,
  templateManagement,
}) => {
  // Form state
  const formMethods = useForm<FormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
  });

  // Rollover tracking state
  const [lastRolledOverFields, setLastRolledOverFields] = useState<
    Record<string, unknown>
  >({});
  const [lastDiscardedFields, setLastDiscardedFields] = useState<string[]>([]);
  const [templateSwitchingActivated, setTemplateSwitchingActivated] =
    useState(false);

  // Use focused hooks
  const templateSwitching = useTemplateSwitching({
    formMethods,
    currentTemplate: templateManagement.currentTemplate,
    setCurrentTemplate: templateManagement.setCurrentTemplate,
    setTemplateTransition: templateManagement.setTemplateTransition,
    setLastRolledOverFields,
    setLastDiscardedFields,
  });

  // Initial values processing
  const {
    optionsMap,
    loadingMap,
    allOptionsLoaded,
    matchInitialValue,
    processedInitialValues,
  } = useInitialFieldData();

  // Track if initial reset has been completed to prevent repeated resets
  const hasInitialResetCompletedRef = useRef(false);

  // Track if we're currently performing initial reset - use state so it triggers re-renders
  const [_isPerformingInitialReset, setIsPerformingInitialReset] =
    useState(false);

  // No longer need post-reset timeouts – reset is synchronous

  // Stabilized reset function to prevent infinite loops
  const performInitialReset = useCallback(
    (values: Record<string, unknown>) => {
      setIsPerformingInitialReset(true);

      // Use reset with values becoming the new default baseline (updates RHF defaults).
      formMethods.reset(values, { keepDirty: false });

      hasInitialResetCompletedRef.current = true;
      initialResetCheckRef.current = false; // Reset the check flag

      // Schedule a micro-task after all TemplateField effects run to
      // promote any programmatically-filled default values to the new
      // defaultValues baseline. This prevents those assignments from
      // marking the form dirty.
      Promise.resolve().then(() => {
        formMethods.reset(formMethods.getValues());
        setIsPerformingInitialReset(false);
      });
    },
    [formMethods]
  );

  // Reference to track if we're in the process of checking for initial reset
  const initialResetCheckRef = useRef(false);

  // Memoize the boolean conversion
  const hasProcessedInitialValues = useMemo(
    () => Boolean(processedInitialValues),
    [processedInitialValues]
  );

  // Handle initial values reset (only once during initial loading)
  // Use a separate effect that only runs when conditions are first met
  useEffect(() => {
    // Only run this effect if we haven't completed the initial reset yet
    if (hasInitialResetCompletedRef.current) {
      return;
    }

    // Prevent multiple concurrent checks
    if (initialResetCheckRef.current) {
      return;
    }

    const shouldAllowReset = !templateSwitchingActivated;

    // Check if we have everything needed for initial reset
    if (processedInitialValues && allOptionsLoaded && shouldAllowReset) {
      initialResetCheckRef.current = true; // Mark that we're starting the reset
      performInitialResetRef.current(processedInitialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProcessedInitialValues, allOptionsLoaded, templateSwitchingActivated]);

  // Separate effect to handle the actual reset function dependency
  const performInitialResetRef = useRef(performInitialReset);
  performInitialResetRef.current = performInitialReset;

  // Remove direct dirty state updates from here since FormEngine now handles it more intelligently
  // This prevents duplicate updates and race conditions

  // Track when template switching is activated
  useEffect(() => {
    const shouldActivate =
      templateSwitching.isLoadingSwitchingTemplate ||
      templateManagement.templateTransition?.isTransitioning;

    if (shouldActivate && !templateSwitchingActivated) {
      setTemplateSwitchingActivated(true);
    }
  }, [
    templateSwitching.isLoadingSwitchingTemplate,
    templateManagement.templateTransition?.isTransitioning,
    templateSwitchingActivated,
  ]);

  // Cleanup on unmount – only need to reset local refs now
  useEffect(() => {
    return () => {
      hasInitialResetCompletedRef.current = false;
      initialResetCheckRef.current = false;
      setIsPerformingInitialReset(false);

      // Cleanup completed
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate effective loading state
  const effectiveIsLoading =
    templateManagement.isLoadingTemplate ||
    templateSwitching.isLoadingSwitchingTemplate;

  const templateContextValue = useMemo<TemplateContextValue>(
    () => ({
      currentTemplate: templateManagement.currentTemplate,
      templateTransition: templateManagement.templateTransition,
      isLoadingTemplate: effectiveIsLoading,
      templateError:
        templateManagement.templateError ||
        templateSwitching.switchingTemplateError,
      // switchTemplate is no longer needed - switching happens automatically
      switchTemplate: () => {
        console.warn(
          "[FormEngineProvider] switchTemplate is deprecated - template switching is now automatic"
        );
      },
    }),
    [
      templateManagement.currentTemplate,
      templateManagement.templateTransition,
      effectiveIsLoading,
      templateManagement.templateError,
      templateSwitching.switchingTemplateError,
    ]
  );

  const initialValuesContextValue = useMemo<InitialValuesContextValue>(
    () => ({
      optionsMap,
      loadingMap,
      allOptionsLoaded,
      matchInitialValue,
      processedInitialValues,
    }),
    [
      optionsMap,
      loadingMap,
      allOptionsLoaded,
      matchInitialValue,
      processedInitialValues,
    ]
  );

  const formMethodsContextValue = useMemo<FormMethodsContextValue>(
    () => ({ formMethods }),
    [formMethods]
  );

  const fieldRolloverContextValue = useMemo<FieldRolloverContextValue>(
    () => ({
      lastRolledOverFields,
      lastDiscardedFields,
    }),
    [lastRolledOverFields, lastDiscardedFields]
  );

  return (
    <TemplateContext.Provider value={templateContextValue}>
      <InitialValuesContext.Provider value={initialValuesContextValue}>
        <FormMethodsContext.Provider value={formMethodsContextValue}>
          <FieldRolloverContext.Provider value={fieldRolloverContextValue}>
            <FormProvider {...formMethods}>{children}</FormProvider>
          </FieldRolloverContext.Provider>
        </FormMethodsContext.Provider>
      </InitialValuesContext.Provider>
    </TemplateContext.Provider>
  );
};

// Main provider that wraps InitialFieldDataProvider
export const FormEngineProvider: React.FC<FormEngineProviderProps> = ({
  children,
  initialValues,
}) => {
  const auth = useAdvisorRequestAuth();

  // Template management (single instance)
  const templateManagement = useTemplateManagement({ initialValues, auth });

  // Show loading state while template is being fetched
  if (templateManagement.isLoadingTemplate) {
    return null;
  }

  // Log error and return null if template failed to load
  if (
    templateManagement.templateError ||
    !templateManagement.mainTemplateData
  ) {
    console.error("[FormEngineProvider] Error loading template", {
      templateError: templateManagement.templateError,
      hasMainTemplateData: Boolean(templateManagement.mainTemplateData),
    });
    return null;
  }

  return (
    <InitialFieldDataProvider
      template={templateManagement.mainTemplateData}
      initialValues={initialValues}
    >
      <FormEngineProviderCore templateManagement={templateManagement}>
        {children}
      </FormEngineProviderCore>
    </InitialFieldDataProvider>
  );
};

// Re-export the optimized context hooks for convenience (for future use)
/* eslint-disable import/no-unused-modules */
export {
  useTemplateContext,
  useInitialValuesContext,
  useFormMethodsContext,
  useFieldRolloverContext,
};
/* eslint-enable import/no-unused-modules */
